import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { ChemistryControllerHost } from '@adornis/chemistry/controllers/chemistry-controller.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { Course } from '@chancenwerk/shared/courses/Course.js';
import { ChancenwerkUserFilter } from '@chancenwerk/shared/filter/ChancenwerkUserFilter.js';
import { KPFAspect } from '@chancenwerk/shared/master-data-management/kpf/entities/aspects/KPFAspect.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { UserAPI } from '@chancenwerk/shared/master-data-management/users/api/UserAPI.js';
import { StudentTypeAspect } from '@chancenwerk/shared/master-data-management/users/entities/aspects/StudentTypeAspect.js';
import { ExternerMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '@chancenwerk/shared/master-data-management/users/entities/Student.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { ChancenRoleAspect } from '@chancenwerk/shared/semantics/ChancenRoleAspect.js';
import { Role } from '@chancenwerk/shared/semantics/Role.js';
import { StudentType } from '@chancenwerk/enums/enums.js';
import { map, type Observable, switchMap } from 'rxjs';

/**
 * Options for the course detail mixin.
 */
interface CourseDetailMixinOptions {
  /**
   * Course class of courses that should be fetched.
   */
  courseClass: string;

  /**
   * Mapper function to map/chunk courses by a specific key.
   */
  courseMapper?: Maybe<(course: Course) => string>;

  /**
   * Selection sets for the different entities. If a selection set is not provided, the corresponding controller will be null.
   */
  selectionSets: {
    kpf: BaseQLSelectionSet<KPF>;
    course: BaseQLSelectionSet<Course>;
    leko?: Maybe<BaseQLSelectionSet<Mitarbeiter>>;
    diku?: Maybe<BaseQLSelectionSet<Mitarbeiter>>;
    os?: Maybe<BaseQLSelectionSet<Student>>;
    us?: Maybe<BaseQLSelectionSet<Student>>;
  };
}

/**
 * Default course mapper which returns the course ID, so that courses are grouped by their ID.
 * @param course Course to map
 * @returns Course ID
 */
function defaultCourseMapper(course: Course) {
  return course._id;
}

/**
 * Helper function to create a student controller by type.
 * @param host Controller host
 * @param kpf KPF controller
 * @param type Student type
 * @param selectionSet Selection set
 * @returns Student controller
 */
function studentControllerByType(
  host: ChemistryControllerHost,
  kpf: RXController<Observable<Maybe<KPF>>>,
  type: StudentType,
  selectionSet: Maybe<BaseQLSelectionSet<Student>>,
) {
  if (!selectionSet) {
    return null;
  }

  return new RXController(
    host,
    kpf.observable.pipe(
      switchMap(
        kpf =>
          UserAPI.subscribeByFilter(
            0,
            0,
            new ChancenwerkUserFilter({
              userClass: [Student._class],
              aspects: [
                new BooleanAspect({ customID: 'active', value: true }),
                new KPFAspect({ value: kpf?._id ?? '' }),
                new StudentTypeAspect({ value: type }),
              ],
            }),
          )(selectionSet ?? {}) as Observable<Student[]>,
      ),
    ),
  );
}

/**
 * Creates a course detail mixin. This is data that is commonly used in course detail views.
 * @param host Controller host
 * @param kpfID Observable of the KPF ID
 * @param options Options
 */
export function createCourseDetailMixin(
  host: ChemistryControllerHost,
  kpfID: Observable<Maybe<string>>,
  options: CourseDetailMixinOptions,
) {
  const kpf = new RXController(
    host,
    kpfID.pipe(switchMap(id => KPF.getByID<KPF>(id ?? '')(options.selectionSets.kpf))),
  );
  const courses = new RXController(
    host,
    kpf.observable.pipe(
      switchMap(kpf =>
        Course.subscribeCoursesByKPFID([kpf?._id ?? ''], options.courseClass)(options.selectionSets.course),
      ),
    ),
  );
  const courseMap = new RXController(
    host,
    courses.observable.pipe(
      map(courses => {
        return courses?.reduce((map, course) => {
          const id = options.courseMapper?.(course) ?? defaultCourseMapper(course);
          const existing = map.get(id);
          if (existing) {
            existing.push(course);
          } else {
            map.set(id, [course]);
          }
          return map;
        }, new Map<string, Course[]>());
      }),
    ),
  );

  return {
    kpf,
    courses,
    courseMap,
    users: {
      os: studentControllerByType(host, kpf, StudentType.OS, options.selectionSets.os),
      us: studentControllerByType(host, kpf, StudentType.US, options.selectionSets.us),
      lekos: options.selectionSets.leko
        ? new RXController(
            host,
            kpf.observable.pipe(
              switchMap(
                kpf =>
                  UserAPI.subscribeByFilter(
                    0,
                    0,
                    new ChancenwerkUserFilter({
                      userClass: [VerwaltungsMitarbeiter._class, Mitarbeiter._class, ExternerMitarbeiter._class],
                      aspects: [
                        new ChancenRoleAspect({ value: Role.LEKO }),
                        new BooleanAspect({ customID: 'active', value: true }),
                        new KPFAspect({ value: kpf?._id ?? '' }),
                      ],
                    }),
                  )(options.selectionSets.leko ?? {}) as Observable<Mitarbeiter[]>,
              ),
            ),
          )
        : null,
      dikus: options.selectionSets.diku
        ? new RXController(
            host,
            kpf.observable.pipe(
              switchMap(
                () =>
                  UserAPI.subscribeByFilter(
                    0,
                    0,
                    new ChancenwerkUserFilter({
                      userClass: [Mitarbeiter._class, VerwaltungsMitarbeiter._class, ExternerMitarbeiter._class],
                      aspects: [
                        new BooleanAspect({ customID: 'active', value: true }),
                        new ChancenRoleAspect({ value: Role.DKL }),
                      ],
                    }),
                  )(options.selectionSets.diku ?? {}) as Observable<Mitarbeiter[]>,
              ),
            ),
          )
        : null,
    },
  };
}
